/**
 * Shared QueryClient instance - imported by both main.tsx (provider)
 * and authStore.ts (cache clearing on logout).
 *
 * This module MUST NOT import any React components or stores
 * to avoid circular dependencies.
 */
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { handleAuthError } from '@/utils/authErrorHandler';
import { ErrorType } from '@/utils/errorClassifier';

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      // Global auth error handler for queries
      handleAuthError(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: unknown) => {
      // Global auth error handler for mutations
      handleAuthError(error);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error: unknown) => {
        const classifiedError = error as { type?: ErrorType; shouldLogout?: boolean };

        // Don't retry auth errors (session expired)
        if (classifiedError.shouldLogout) return false;

        // Retry network errors and auth service down errors up to 3 times
        if (classifiedError.type === ErrorType.NETWORK_ERROR || classifiedError.type === ErrorType.AUTH_SERVICE_DOWN) {
          return failureCount < 3;
        }

        // Don't retry other errors (server errors, unknown errors)
        return false;
      },
      retryDelay: (attemptIndex) => {
        // Exponential backoff: 1s, 2s, 4s, 8s (max 10s)
        return Math.min(1000 * 2 ** attemptIndex, 10000);
      },
    },
  },
});
